import React from 'react';
import {useTranslation} from 'react-i18next';
import {isPlainObject} from '../shapes';
import {humanizeFieldLabel} from '../humanize';

// Eventos simples: el detail trae 1-3 campos y el panel es una sola frase (o una fila de
// diff). Un componente configurable por event_type — cada config sabe qué campo leer y
// cómo contarlo. Los eventos cuyo detail no agrega NADA sobre el título no se registran
// acá: van con NO_DETAIL y ni muestran el toggle "Ver detalle".

const RAW = {interpolation: {escapeValue: false}};

function textOf(value) {
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function Story({children}) {
    return <div className="ald-story">{children}</div>;
}

// Fila de diff suelta (label + viejo → nuevo) para renombres.
function RenameRow({label, before, after}) {
    return (
        <div className="ald-diff">
            <div className="ald-diff-row">
                <span className="ald-diff-label">{label}</span>
                <span className="ald-diff-values">
                    <span className="ald-diff-before">{before || '—'}</span>
                    <span className="ald-diff-arrow" aria-hidden="true">→</span>
                    <span className="ald-diff-after">{after || '—'}</span>
                </span>
            </div>
        </div>
    );
}

function commentPreview(detail, t) {
    const preview = textOf(detail.comment_preview);
    if (!preview) return null;
    return <Story>{preview}</Story>;
}

function sessionRenamed(detail, t) {
    const before = textOf(detail.old_name);
    const after = textOf(detail.new_name);
    if (!before && !after) return null;
    return <RenameRow label={humanizeFieldLabel('name', t)} before={before} after={after}/>;
}

function webhookSent(detail, t) {
    const event = textOf(detail.event_type);
    const success = detail.success;
    if (success === true) {
        return <Story>{event
            ? t('activity_log.detail.webhook_sent_ok', {event, ...RAW})
            : t('activity_log.detail.webhook_ok')}</Story>;
    }
    if (success === false) {
        return <Story>{event
            ? t('activity_log.detail.webhook_sent_failed', {event, ...RAW})
            : t('activity_log.detail.webhook_failed')}</Story>;
    }
    // Sin `success` no se afirma nada sobre el resultado: solo qué evento se disparó.
    if (!event) return null;
    return <Story>{t('activity_log.detail.webhook_event', {event, ...RAW})}</Story>;
}

function invitedEmail(detail, t) {
    const email = textOf(detail.invited_email);
    if (!email) return null;
    return <Story>{t('activity_log.detail.invited_email', {email, ...RAW})}</Story>;
}

function removedEmail(detail, t) {
    const email = textOf(detail.removed_email);
    if (!email) return null;
    return <Story>{t('activity_log.detail.removed_email', {email, ...RAW})}</Story>;
}

// «Avisarle al equipo» desde «Inversión sin control»: a quién se le avisó y la nota, si la hubo. La
// cuenta y quién avisó ya están en el título (`description` del backend).
function uncontrolledFlagged(detail, t) {
    const names = Array.isArray(detail.recipient_names) ? detail.recipient_names.filter(textOf) : [];
    if (!names.length) return null;
    const note = textOf(detail.note);
    return (
        <Story>
            {t('activity_log.detail.uncontrolled_flagged', {names: names.join(', '), ...RAW})}
            {note && <><br/>«{note}»</>}
        </Story>
    );
}

const CONFIGS = {
    ITEM_COMMENT_UPDATED: commentPreview,
    AGENT_SESSION_RENAMED: sessionRenamed,
    WEBHOOK_SENT: webhookSent,
    USER_INVITED: invitedEmail,
    USER_REMOVED: removedEmail,
    UNCONTROLLED_SPEND_FLAGGED: uncontrolledFlagged,
};

export function SimpleFieldsDetail({detail, entry}) {
    const {t} = useTranslation();
    if (!isPlainObject(detail)) return null;
    const render = CONFIGS[entry && entry.event_type];
    // Registrado por error o event_type desconocido: mejor nada que un panel vacío.
    if (!render) return null;
    return render(detail, t);
}

// Guards del registry, uno por evento: si el campo que da sentido al panel no vino,
// el evento degrada a la resolución por forma en vez de mostrar un panel hueco.
export function canRenderCommentPreview(detail) {
    return isPlainObject(detail) && !!textOf(detail.comment_preview);
}

export function canRenderSessionRename(detail) {
    return isPlainObject(detail) && (!!textOf(detail.old_name) || !!textOf(detail.new_name));
}

export function canRenderWebhookSent(detail) {
    return isPlainObject(detail) && (typeof detail.success === 'boolean' || !!textOf(detail.event_type));
}

export function canRenderInvitedEmail(detail) {
    return isPlainObject(detail) && !!textOf(detail.invited_email);
}

export function canRenderRemovedEmail(detail) {
    return isPlainObject(detail) && !!textOf(detail.removed_email);
}

export function canRenderUncontrolledFlagged(detail) {
    return isPlainObject(detail) && Array.isArray(detail.recipient_names) && detail.recipient_names.some(textOf);
}
